import { Dialog, FileUpload, Portal, Progress } from "@ark-ui/react";
import {
  DELETION_FLAG,
  ERROR_CODE,
  type Provider,
  SENSOR_TYPE,
  SensorBatchRegistrationHeaderRows,
  SensorBatchRegistrationRowSchema,
  UniversalError,
  toSensorBatchRegistrationRow,
} from "@packages/core";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import Encoding from "encoding-japanese";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuDownload, LuFile, LuTrash } from "react-icons/lu";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";
import {
  ButtonStyle,
  DialogStyle,
  FileUploadStyle,
  FormField,
  PageName,
  PaperStyle,
  ProviderSelector,
  TypographyStyle,
  progressStyles,
  showError,
  showMessage,
} from "#components";
import {
  createSensor,
  modifySensor,
  removeSensorsCache,
  saveAsFile,
  updateSensorQueryData,
  useFacilityQuery,
  useListedSensors,
  useProviderInfiniteQuery,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/sensors/batch-registration",
)({ component: Component });

const fileUploadStyle = FileUploadStyle();

function Component() {
  const { facilityKey } = Route.useParams();

  const { data: facility } = useFacilityQuery(facilityKey);
  const importing = useRef(false);
  const router = useRouter();
  const translate = useTranslate();
  const [files, setFiles] = useState<Array<File>>([]);
  const [progress, setProgress] = useState(100);

  const headerRows = useMemo(
    function () {
      return SensorBatchRegistrationHeaderRows({ translate });
    },
    [translate],
  );

  const schema = useMemo(
    function () {
      return SensorBatchRegistrationRowSchema({ translate });
    },
    [translate],
  );

  const { loading, pages } = useListedSensors({ deleted: DELETION_FLAG.DEFAULT, facilityKey });

  const sensors = useMemo(
    function () {
      return (
        pages.flatMap(function (page) {
          return page.sensors;
        }) ?? []
      );
    },
    [pages],
  );

  const [provider, setProvider] = useState<Provider | undefined>(undefined);
  const providerPaginator = useProviderInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (providerPaginator.hasNextPage && !providerPaginator.isFetching) {
        providerPaginator.fetchNextPage();
      }
    },
    [providerPaginator],
  );

  const providers = useMemo(
    function () {
      return (
        providerPaginator.data?.pages.flatMap(function (page) {
          return page.providers;
        }) ?? []
      );
    },
    [providerPaginator.data],
  );

  const dialogClasses = DialogStyle();
  const progressClasses = progressStyles();

  return (
    <main
      className={css({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
    >
      <PageName>
        {facility
          ? translate("%SensorBatchRegistration.0%") + " - " + facility.name
          : translate("%Loading.0%")}
      </PageName>

      <Dialog.Root open={progress !== 100}>
        <Portal>
          <Dialog.Backdrop className={dialogClasses.backdrop} />
          <Dialog.Positioner className={dialogClasses.positioner}>
            <Dialog.Content asChild>
              <Progress.Root className={progressClasses.root} value={progress}>
                <Progress.Label className={progressClasses.label}>
                  {translate("%Processing.0%")}
                </Progress.Label>
                <Progress.Track className={progressClasses.track}>
                  <Progress.Range className={progressClasses.range} />
                </Progress.Track>
                <Progress.ValueText className={progressClasses.valueText} />
              </Progress.Root>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          maxWidth: "480px",
          padding: { smDown: "16px", sm: "16px 24px" },
          width: "stretch",
        })}
      >
        <h1
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {translate("%SensorBatchRegistration.1%")}
        </h1>
        <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
          {sensors.length > 0
            ? translate("%SensorBatchRegistration.3%")
            : translate("%SensorBatchRegistration.2%")}
        </p>
        {sensors.length > 0 && (
          <p className={TypographyStyle({ color: "error", fontSize: "14px" })}>
            {translate("%SensorBatchRegistration.4%")}
          </p>
        )}

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          1.&nbsp;{translate("%SelectProvider.0%")}&nbsp;({translate("%Optional.1%")})
        </h2>

        <FormField
          className={css({ marginTop: "8px", width: "stretch" })}
          description={translate("%SensorBatchRegistration.9%")}
        >
          <ProviderSelector
            className={css({ maxWidth: "320px", width: "100%" })}
            load={async function () {
              await providerPaginator.fetchNextPage();
            }}
            loaded={!providerPaginator.hasNextPage}
            loading={providerPaginator.isLoading}
            onChange={function (provider) {
              setProvider(provider ?? undefined);
            }}
            options={providers}
            title={translate("%Provider.0%")}
            value={provider ?? null}
          />
        </FormField>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          2.&nbsp;{sensors.length > 0 ? translate("%Download.2%") : translate("%Download.1%")}
        </h2>
        <div className={css({ marginTop: "8px" })}>
          <button
            className={ButtonStyle({
              appearance: "contained",
              borderRadius: "4px",
              color: "secondary",
              flexGrow: 1,
              fontSize: "14px",
              gap: "8px",
              padding: "10px 16px",
              width: "stretch",
            })}
            disabled={loading}
            onClick={async function () {
              saveAsFile(
                new Blob(
                  [
                    new Uint8Array(
                      Encoding.convert(
                        Encoding.stringToCode(
                          headerRows
                            .concat(
                              sensors.map(function (sensor) {
                                return toSensorBatchRegistrationRow(sensor, { translate });
                              }),
                            )
                            .join("\r\n"),
                        ),
                        { from: "UNICODE", to: "SJIS" },
                      ),
                    ),
                  ],
                  { type: "text/csv" },
                ),
                translate("%SensorBatchRegistration.5%", {
                  $: { facility, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
                }) + ".csv",
              );
            }}
          >
            <LuDownload />
            <span>{translate("%Download.0%")}</span>
          </button>
        </div>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          3.&nbsp;{translate("%Upload.1%")}
        </h2>

        <div className={css({ marginTop: "8px" })}>
          <FileUpload.Root
            acceptedFiles={files}
            className={fileUploadStyle.root}
            maxFiles={3}
            onFileChange={function ({ acceptedFiles }) {
              setFiles(acceptedFiles);
            }}
          >
            <FileUpload.Dropzone className={fileUploadStyle.dropzone}>
              <span className={css({ pointerEvents: "none" })}>{translate("%DragAndDrop.0%")}</span>
              <span className={css({ pointerEvents: "none" })}>{translate("%Or.0%")}</span>
              <span className={css({ pointerEvents: "none" })}>{translate("%Select.1%")}</span>
            </FileUpload.Dropzone>
            <FileUpload.ItemGroup className={fileUploadStyle.itemGroup}>
              <FileUpload.Context>
                {function ({ acceptedFiles }) {
                  return acceptedFiles.map(function (file, index) {
                    return (
                      <FileUpload.Item className={fileUploadStyle.item} file={file} key={index}>
                        <FileUpload.ItemPreview type="image/*">
                          <FileUpload.ItemPreviewImage />
                        </FileUpload.ItemPreview>
                        <FileUpload.ItemPreview type=".*">
                          <LuFile className={fileUploadStyle.itemPreviewIcon} />
                        </FileUpload.ItemPreview>
                        <FileUpload.ItemName className={fileUploadStyle.itemName} />
                        <FileUpload.ItemDeleteTrigger className={fileUploadStyle.itemDeleteTrigger}>
                          <LuTrash />
                        </FileUpload.ItemDeleteTrigger>
                      </FileUpload.Item>
                    );
                  });
                }}
              </FileUpload.Context>
            </FileUpload.ItemGroup>
            <FileUpload.HiddenInput />
          </FileUpload.Root>
        </div>

        <div className={css({ display: "flex", gap: "8px", marginTop: "16px" })}>
          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              flexGrow: 1,
              fontSize: "14px",
              padding: "10px 16px",
            })}
            onClick={function (event) {
              if (router.history.canGoBack()) {
                event.preventDefault();
                router.history.back();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/sensors"
          >
            {translate("%Cancel.0%")}
          </Link>
          <button
            className={ButtonStyle({
              appearance: "contained",
              borderRadius: "4px",
              color: "primary",
              flexGrow: 1,
              fontSize: "14px",
              padding: "10px 16px",
            })}
            disabled={files.length === 0}
            onClick={async function () {
              if (importing.current) {
                return;
              }

              if (!facility) {
                showError(
                  new UniversalError(
                    ERROR_CODE.FACILITY_NOT_FOUND,
                    "施設情報の取得が完了していないため、センサーの一括登録を実行できません。",
                  ),
                );
                return;
              }

              importing.current = true;

              const failedRows = [];
              for (let f = 0; f < files.length; f++) {
                const file = files[f];
                let rows;
                try {
                  const bytes = new Uint8Array(await file.arrayBuffer());
                  const t = Encoding.convert(bytes, { from: "SJIS", to: "UNICODE" });
                  const text = Encoding.codeToString(t) as string;
                  rows = text.split(/\r\n|\n|\r/);
                } catch (error) {
                  console.error(error);
                  showMessage({
                    body: translate("%Import.1%", { $: { file } }),
                    color: "error",
                    title: translate("%Error.0%"),
                    icon: undefined,
                    type: "toast",
                  });
                  continue;
                }

                console.info("ファイルを読み込みました。", rows);

                setProgress(0);

                for (let r = headerRows.length; r < rows.length; r++) {
                  const row = rows[r];

                  let data;
                  try {
                    data = v.parse(schema, row.split(","));
                  } catch (error) {
                    console.error(error);
                    showMessage({
                      body: translate("%Import.2%", { $: { file, row: r + 1 } }),
                      color: "error",
                      title: translate("%Error.0%"),
                      icon: undefined,
                      type: "toast",
                    });
                    failedRows.push(row);
                    continue;
                  }

                  let providerKey;
                  if (data.providerKey) {
                    providerKey = data.providerKey;
                  } else if (provider) {
                    providerKey = provider.providerKey;
                  } else {
                    showMessage({
                      body: translate("%Import.2%", { $: { file, row: r + 1 } }),
                      color: "error",
                      title: translate("%Error.0%"),
                      icon: undefined,
                      type: "toast",
                    });
                    failedRows.push(row);
                    continue;
                  }

                  try {
                    let result;
                    if (data.sensorKey) {
                      result = await modifySensor({ properties: data, sensorKey: data.sensorKey });
                    } else {
                      switch (data.type) {
                        case SENSOR_TYPE.AAMS: {
                          result = await createSensor({
                            facilityKey: facility.facilityKey,
                            organizationKey: facility.organizationKey,
                            providerKey,
                            name: data.name,
                            type: data.type,
                            macAddress: data.macAddress,
                          });
                          break;
                        }
                        case SENSOR_TYPE.ANSIEL: {
                          result = await createSensor({
                            facilityKey: facility.facilityKey,
                            organizationKey: facility.organizationKey,
                            providerKey,
                            name: data.name,
                            type: data.type,
                            serialNumber: data.serialNumber,
                            pushNotificationEnabled: true,
                          });
                          break;
                        }
                        default: {
                          throw new UniversalError(
                            ERROR_CODE.UNEXPECTED,
                            "一括登録に対応していないセンサー種別です。",
                          );
                        }
                      }
                    }

                    updateSensorQueryData(result.sensor.sensorKey, result.sensor);
                  } catch (error) {
                    console.error(error);
                    showMessage({
                      body: translate("%Import.2%", { $: { file, row: r + 1 } }),
                      color: "error",
                      title: translate("%Error.0%"),
                      icon: undefined,
                      type: "toast",
                    });
                    failedRows.push(row);
                    continue;
                  }

                  showMessage({
                    body: translate("%Import.4%", { $: { file, row: r + 1 } }),
                    color: "success",
                    title: translate("%Success.0%"),
                    icon: undefined,
                    type: "toast",
                  });

                  setProgress(Math.floor(((r + 1) / rows.length) * 100));
                }

                setProgress(100);
              }

              if (failedRows.length > 0) {
                saveAsFile(
                  new Blob(
                    [
                      new Uint8Array(
                        Encoding.convert(
                          Encoding.stringToCode(headerRows.concat(failedRows).join("\r\n")),
                          { from: "UNICODE", to: "SJIS" },
                        ),
                      ),
                    ],
                    { type: "text/csv" },
                  ),
                  translate("%SensorBatchRegistration.6%", {
                    $: { facility, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
                  }),
                );
              }

              removeSensorsCache();

              setFiles([]);

              importing.current = false;
            }}
          >
            {translate("%Import.0%")}
          </button>
        </div>
      </div>
    </main>
  );
}
